import { AnimatePresence, motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { X, Minus, Plus } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useLanguage } from "../i18n/LanguageContext";
import { usePortal } from "../context/PortalContext";
import { fetchProducts, fetchStoreSettings } from "../commerce";
import { fmtPrice } from "../lib/format";

function isRtl() {
  return typeof document !== "undefined" && document.documentElement.dir === "rtl";
}

export default function CartDrawer() {
  const { isOpen, closeCart, items, setQty, removeItem } = useCart();
  const { t, lang } = useLanguage();
  const { go } = usePortal();
  const { data: products = [] } = useQuery({ queryKey: ["products"], queryFn: fetchProducts, staleTime: 60000 });
  const { data: settings } = useQuery({ queryKey: ["store-settings"], queryFn: fetchStoreSettings, staleTime: 60000 });

  const detailed = items
    .map((item) => ({ ...item, product: products.find((p) => p.handle === item.handle) }))
    .filter((i) => i.product);

  const subtotal = detailed.reduce((sum, i) => sum + (i.product.price || 0) * i.qty, 0);
  const threshold = settings?.free_shipping_threshold ?? 2500;
  const freeUnlocked = subtotal > threshold;
  const remaining = Math.max(0, threshold - subtotal);
  const progress = Math.min(1, subtotal / threshold);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            onClick={closeCart}
            className="fixed inset-0 z-[80] bg-xir-dark/70 backdrop-blur-sm"
            data-testid="cart-overlay"
          />
          <motion.aside
            initial={{ x: isRtl() ? "-100%" : "100%" }}
            animate={{ x: "0%" }}
            exit={{ x: isRtl() ? "-100%" : "100%" }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-y-0 z-[90] flex w-full max-w-md flex-col border-s hairline bg-xir-surface text-xir-paper end-0"
            data-testid="cart-drawer"
            role="dialog"
            aria-label={t("cart.title")}
          >
            <div className="flex items-center justify-between border-b hairline px-7 py-6">
              <h2 className="font-display text-2xl font-medium" data-testid="cart-title">
                {t("cart.title")}
              </h2>
              <button onClick={closeCart} data-testid="cart-close-button" aria-label={t("nav.close")} className="text-xir-paper/60 hover:text-xir-paper">
                <X size={20} strokeWidth={1.5} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-7 py-6">
              {detailed.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center gap-6 text-center">
                  <p className="text-xir-paper/60" data-testid="cart-empty-message">
                    {t("cart.empty")}
                  </p>
                  <button
                    onClick={() => { closeCart(); go("/shop"); }}
                    data-testid="cart-empty-cta"
                    className="border hairline px-8 py-3 text-xs uppercase tracking-[0.22em] text-xir-paper transition-colors hover:border-xir-gold/50 hover:text-xir-gold"
                  >
                    {t("cart.emptyCta")}
                  </button>
                </div>
              ) : (
                <ul className="divide-y divide-xir-gold/10">
                  {detailed.map(({ product, qty, grind, key }) => {
                    const k = key || product.handle;
                    return (
                    <li key={k} className="flex items-center gap-4 py-6" data-testid={`cart-item-${k.replace(":", "-")}`}>
                      <img src={`/media/bags/bag-${product.tint}.webp`} alt="" className="h-16 w-auto object-contain" />
                      <div className="flex-1">
                        <p className="font-display text-lg font-medium">{product.name[lang] || product.name.en}</p>
                        <p className="mt-1 text-xs tracking-[0.14em] text-xir-paper/40">
                          {product.weight} · {t(`product.grind.${grind || "whole"}`)} · {fmtPrice(product.price, lang)}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <button onClick={() => setQty(k, qty - 1)} data-testid={`cart-qty-minus-${k.replace(":", "-")}`} className="flex h-7 w-7 items-center justify-center border hairline text-xir-paper/60 hover:text-xir-paper" aria-label="decrease">
                          <Minus size={12} />
                        </button>
                        <span className="w-5 text-center text-sm" data-testid={`cart-qty-${k.replace(":", "-")}`}>{qty}</span>
                        <button onClick={() => setQty(k, qty + 1)} data-testid={`cart-qty-plus-${k.replace(":", "-")}`} className="flex h-7 w-7 items-center justify-center border hairline text-xir-paper/60 hover:text-xir-paper" aria-label="increase">
                          <Plus size={12} />
                        </button>
                      </div>
                      <button onClick={() => removeItem(k)} data-testid={`cart-remove-${k.replace(":", "-")}`} className="text-xs text-xir-paper/40 underline-offset-4 hover:text-xir-gold hover:underline">
                        {t("cart.remove")}
                      </button>
                    </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {detailed.length > 0 && (
              <div className="border-t hairline px-7 py-6">
                {/* free delivery progress — real, from server settings */}
                <div className="mb-5">
                  <p className="mb-2 text-xs tracking-wide text-xir-paper/60" data-testid="free-shipping-message">
                    {freeUnlocked
                      ? t("cart.freeUnlocked")
                      : t("cart.freeProgress").replace("{amount}", fmtPrice(remaining, lang))}
                  </p>
                  <div className="h-px w-full bg-xir-paper/15">
                    <div
                      className="h-px bg-xir-gold transition-[width] duration-700"
                      style={{ width: `${Math.min(100, progress * 100)}%` }}
                      data-testid="free-shipping-bar"
                    />
                  </div>
                </div>
                <div className="mb-2 flex items-center justify-between text-sm">
                  <span className="text-xir-paper/60">{t("cart.subtotal")}</span>
                  <span className="font-display text-lg text-xir-gold" data-testid="cart-subtotal">
                    {fmtPrice(subtotal, lang)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs text-xir-paper/45">
                  <span>{t("cart.shipping")}</span>
                  <span data-testid="cart-shipping">
                    {freeUnlocked
                      ? t("cart.free")
                      : settings?.shipping_fee != null
                        ? fmtPrice(settings.shipping_fee, lang)
                        : t("cart.shippingPending")}
                  </span>
                </div>
                <button
                  onClick={() => { closeCart(); go("/checkout"); }}
                  data-testid="checkout-button"
                  className="mt-5 w-full border border-xir-gold/40 py-4 text-xs uppercase tracking-[0.25em] text-xir-gold transition-colors hover:bg-xir-gold hover:text-xir-dark"
                >
                  {t("cart.checkout")}
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
